import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowPathIcon, ArrowLeftIcon, CurrencyDollarIcon, SparklesIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

function SpinWheel() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [wonPrize, setWonPrize] = useState(null);
  const [userCoins, setUserCoins] = useState(user?.coins || 0);
  const wheelRef = useRef(null);

  useEffect(() => {
    if (user?.coins !== undefined) {
      setUserCoins(user.coins);
    }
  }, [user?.coins]);

  const prizes = [
    { value: 50, color: 'from-blue-500 to-blue-600', text: 'text-blue-100' },
    { value: 100, color: 'from-purple-500 to-purple-600', text: 'text-purple-100' },
    { value: 20, color: 'from-pink-500 to-pink-600', text: 'text-pink-100' },
    { value: 200, color: 'from-green-500 to-green-600', text: 'text-green-100' },
    { value: 10, color: 'from-yellow-500 to-yellow-600', text: 'text-yellow-100' },
    { value: 500, color: 'from-red-500 to-red-600', text: 'text-red-100' }
  ];

  const calculatePrize = (totalRotation) => {
    const normalizedRotation = totalRotation % 360;
    const sectionSize = 360 / prizes.length;
    const prizeIndex = Math.floor(normalizedRotation / sectionSize);
    return prizes[prizeIndex];
  };

  const handleSpin = async () => {
    if (isSpinning) return;
    
    if (!user?.coins || user.coins < 10) {
      toast.error('Not enough coins! You need 10 coins to spin.');
      return;
    }

    try {
      setIsSpinning(true);
      
      // Deduct coins first
      const updatedCoins = userCoins - 10;
      const updateResult = await updateUser({
        coins: updatedCoins,
        spinsPlayed: (user?.spinsPlayed || 0) + 1
      });

      if (!updateResult.success) {
        throw new Error(updateResult.error || 'Failed to update user coins');
      }

      setUserCoins(updatedCoins);
      
      // Generate a random number of full rotations (5-10) plus a random degree
      const fullRotations = 5 + Math.floor(Math.random() * 5);
      const randomDegree = Math.floor(Math.random() * 360);
      const spinDegrees = (fullRotations * 360) + randomDegree;
      
      setRotation(rotation + spinDegrees);

      const prize = calculatePrize(rotation + spinDegrees);

      setTimeout(async () => {
        try {
          setWonPrize(prize.value);
          setShowResult(true);
          setIsSpinning(false);

          const finalCoins = updatedCoins + prize.value;
          const finalUpdate = await updateUser({
            coins: finalCoins,
            totalWinnings: (user?.totalWinnings || 0) + prize.value
          });

          if (!finalUpdate.success) {
            throw new Error(finalUpdate.error || 'Failed to update final coins');
          }

          setUserCoins(finalCoins);
          toast.success(`Congratulations! You won ${prize.value} coins!`);
        } catch (error) {
          toast.error('Error updating prize. Please contact support.');
          await updateUser({
            coins: userCoins
          });
          setUserCoins(userCoins);
        }
      }, 5000);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
      setIsSpinning(false);
    }
  };

  const handleSpinAgain = () => {
    if (userCoins < 10) {
      toast.error('Not enough coins! You need 10 coins to spin.');
      return;
    }
    setShowResult(false);
    setWonPrize(null);
  };

  if (showResult) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#f56756] to-[#402e2c] p-6">
        <div className="bg-[#b34e42]/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 text-center space-y-6 border border-[#402e2c]/20">
          <div className="flex justify-center">
            <div className="bg-gradient-to-r from-[#402e2c] to-[#402e2c]/80 rounded-full p-4 shadow-lg animate-bounce">
              <SparklesIcon className="h-12 w-12 text-[#f56756]" />
            </div>
          </div>
          
          <h2 className="text-2xl font-bold text-white">
            Congratulations!
          </h2>
          
          <div className="space-y-4">
            <p className="text-xl text-[#402e2c] font-semibold">
              You won {wonPrize} coins!
            </p>
            <p className="text-gray-300">
              New balance: {userCoins} coins
            </p>
          </div>

          <div className="space-y-4 pt-4">
            <button
              onClick={handleSpinAgain}
              disabled={userCoins < 10}
              className={`w-full bg-gradient-to-r from-[#402e2c] to-[#402e2c]/80 text-white rounded-full py-3.5 text-base font-medium shadow-lg hover:shadow-[#402e2c]/20 transition-all duration-300 ${
                userCoins < 10 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              Spin Again
            </button>
            
            <button
              onClick={() => navigate('/home')}
              className="w-full border border-[#402e2c] text-[#402e2c] rounded-full py-3.5 text-base font-medium bg-[#b34e42]/50 hover:bg-[#b34e42] transition-all duration-300"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f56756] to-[#402e2c] p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button 
          onClick={() => navigate('/home')}
          className="p-2 text-white hover:text-[#402e2c] transition-colors duration-300"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <div className="text-[#402e2c] font-medium flex items-center gap-2">
          <CurrencyDollarIcon className="h-5 w-5" />
          {userCoins}
        </div>
      </div>

      {/* Spin Wheel Card */}
      <div className="bg-[#b34e42]/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 text-center space-y-6 border border-[#402e2c]/20">
        <h2 className="text-2xl font-bold text-white mb-4">
          Spin & Win
        </h2>
        <p className="text-gray-300">
          Cost: 10 coins per spin
        </p>

        {/* Wheel Container */}
        <div className="relative w-72 h-72 mx-auto my-8" ref={wheelRef}>
          <div
            className="absolute inset-0 rounded-full border-8 border-[#402e2c] overflow-hidden shadow-lg"
            style={{
              transform: `rotate(${rotation}deg)`,
              transition: isSpinning ? 'transform 5s cubic-bezier(0.17, 0.67, 0.83, 0.67)' : 'none'
            }}
          >
            {prizes.map((prize, index) => (
              <div
                key={index}
                className={`absolute w-1/2 h-1/2 origin-bottom-right bg-gradient-to-br ${prize.color}`}
                style={{
                  transform: `rotate(${index * (360 / prizes.length)}deg) skewY(30deg)`,
                  clipPath: 'polygon(0 0, 100% 0, 100% 100%)'
                }}
              >
                <span
                  className={`absolute text-white font-bold text-lg ${prize.text}`}
                  style={{
                    left: '75%',
                    top: '25%',
                    transform: 'translate(-50%, -50%) rotate(75deg)'
                  }}
                >
                  {prize.value}
                </span>
              </div>
            ))}
          </div>
          
          {/* Center pointer */}
          <div 
            className="absolute top-0 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[12px] border-r-[12px] border-t-[24px] border-l-transparent border-r-transparent border-t-red-500"
            style={{ marginTop: '-12px' }}
          />
        </div>

        {/* Spin Button */}
        <button
          onClick={handleSpin}
          disabled={isSpinning || userCoins < 10}
          className={`w-full bg-gradient-to-r from-[#402e2c] to-[#402e2c]/80 text-white rounded-full py-3.5 text-base font-medium flex items-center justify-center gap-2 shadow-lg hover:shadow-[#402e2c]/20 transition-all duration-300 ${
            isSpinning || userCoins < 10 ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          <ArrowPathIcon className={`h-5 w-5 ${isSpinning ? 'animate-spin' : ''}`} />
          {isSpinning ? 'Spinning...' : 'Spin Now'}
        </button>
      </div>
    </div>
  );
}

export default SpinWheel; 